import { Controller } from '@hotwired/stimulus';

const VALID_STATUSES = ['succeeded', 'processing'];

export default class extends Controller {
  static targets = [
    'accountHolderName',
    'sortCode',
    'accountNumber',
    'setupIntentId',
    'setupIntentStatus',
    'error',
    'submit',
  ];

  static values = {
    publishableKey: String,
    clientSecret: String,
    customerEmail: String,
    addressLine1: String,
    addressLine2: String,
    addressCity: String,
    addressPostalCode: String,
    addressCountry: String,
  };

  async connect() {
    if (!this.publishableKeyValue || !this.clientSecretValue) return;

    await this.loadStripeJs();
    this.stripe = Stripe(this.publishableKeyValue);
  }

  async loadStripeJs() {
    if (window.Stripe) return;

    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }

  async submit(event) {
    event.preventDefault();

    if (!this.stripe) {
      this.showError('Direct Debit form is not ready. Please reload the page.');
      return;
    }

    const form = this.element;

    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    this.clearError();
    this.disableSubmit();

    const sortCode = (this.sortCodeTarget.value || '').replace(/-/g, '');
    const accountNumber = (this.accountNumberTarget.value || '').replace(/\s+/g, '');

    const billingDetails = {
      name: this.accountHolderNameTarget.value,
      email: this.customerEmailValue,
      address: {
        line1: this.addressLine1Value || undefined,
        line2: this.addressLine2Value || undefined,
        city: this.addressCityValue || undefined,
        postal_code: this.addressPostalCodeValue || undefined,
        country: this.addressCountryValue || 'GB',
      },
    };

    const { setupIntent, error } = await this.stripe.confirmBacsDebitSetup(
      this.clientSecretValue,
      {
        payment_method: {
          bacs_debit: {
            sort_code: sortCode,
            account_number: accountNumber,
          },
          billing_details: billingDetails,
        },
      }
    );

    if (error) {
      this.showError(error.message || 'We could not set up your Direct Debit. Please check your details and try again.');
      this.enableSubmit();
      return;
    }

    if (!setupIntent || !VALID_STATUSES.includes(setupIntent.status)) {
      this.showError(`Direct Debit mandate not complete: ${setupIntent ? setupIntent.status : 'unknown'}`);
      this.enableSubmit();
      return;
    }

    this.setupIntentIdTarget.value = setupIntent.id;
    this.setupIntentStatusTarget.value = setupIntent.status;

    form.requestSubmit();
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = '';
    }
  }

  disableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }
  }

  enableSubmit() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
    }
  }
}
